import type { Point, Rect } from './camera';
import type { OBB } from './geometry';
import { toLocal, toWorld } from './handles';

export type Side = 'top' | 'right' | 'bottom' | 'left';
/** A point on an object, normalized to its box (0..1 on each axis, before rotation). */
export type Anchor = { nx: number; ny: number };
export type Routing = 'straight' | 'elbow' | 'curved';

export const ANCHOR_SNAP_PX = 16;
/** Anchor dots (drag one to connect): outside each side's midpoint, at a constant screen offset. */
export const ANCHOR_DOT_OFFSET_PX = 12;
export const ANCHOR_DOT_HIT_PX = 8;
const STUB = 24;
const CURVE_SAMPLES = 20;

const MIDPOINTS: [Side, Anchor][] = [
  ['top', { nx: 0.5, ny: 0 }],
  ['right', { nx: 1, ny: 0.5 }],
  ['bottom', { nx: 0.5, ny: 1 }],
  ['left', { nx: 0, ny: 0.5 }],
];
const NORMAL: Record<Side, Point> = { top: { x: 0, y: -1 }, right: { x: 1, y: 0 }, bottom: { x: 0, y: 1 }, left: { x: -1, y: 0 } };

export function anchorPoint(o: OBB, a: Anchor): Point {
  return toWorld(o, { x: (a.nx - 0.5) * o.w, y: (a.ny - 0.5) * o.h });
}

export function anchorDots(o: OBB, zoom: number): { side: Side; anchor: Anchor; at: Point }[] {
  const off = ANCHOR_DOT_OFFSET_PX / zoom;
  return MIDPOINTS.map(([side, anchor]) => {
    const n = NORMAL[side];
    return { side, anchor, at: toWorld(o, { x: (anchor.nx - 0.5) * o.w + n.x * off, y: (anchor.ny - 0.5) * o.h + n.y * off }) };
  });
}

/** The anchor dot within `radiusPx` screen pixels of `p` (the closest), with its world distance; else null. */
export function anchorDotHit(o: OBB, p: Point, zoom: number, radiusPx = ANCHOR_DOT_HIT_PX): { side: Side; dist: number } | null {
  let best: { side: Side; dist: number } | null = null;
  for (const d of anchorDots(o, zoom)) {
    const dist = Math.hypot(d.at.x - p.x, d.at.y - p.y);
    if (dist <= radiusPx / zoom && (!best || dist < best.dist)) best = { side: d.side, dist };
  }
  return best;
}

export function anchorDotAt(o: OBB, p: Point, zoom: number, radiusPx = ANCHOR_DOT_HIT_PX): Side | null {
  return anchorDotHit(o, p, zoom, radiusPx)?.side ?? null;
}

export function sideOf(a: Anchor): Side | null {
  return MIDPOINTS.find(([, m]) => Math.abs(m.nx - a.nx) < 1e-9 && Math.abs(m.ny - a.ny) < 1e-9)?.[0] ?? null;
}

/** The side midpoint within ANCHOR_SNAP_PX screen pixels of `p`, else the closest point of the object's outline. */
export function nearestAnchor(o: OBB, p: Point, zoom: number): Anchor {
  for (const [, m] of MIDPOINTS) {
    const q = anchorPoint(o, m);
    if (Math.hypot(q.x - p.x, q.y - p.y) <= ANCHOR_SNAP_PX / zoom) return m;
  }
  const l = toLocal(o, p);
  const hw = o.w / 2;
  const hh = o.h / 2;
  const x = Math.min(hw, Math.max(-hw, l.x));
  const y = Math.min(hh, Math.max(-hh, l.y));
  // Push the clamped point onto the nearest edge.
  const d = { left: x + hw, right: hw - x, top: y + hh, bottom: hh - y };
  const edge = (Object.keys(d) as Side[]).reduce((a, b) => (d[b] < d[a] ? b : a));
  const ex = edge === 'left' ? -hw : edge === 'right' ? hw : x;
  const ey = edge === 'top' ? -hh : edge === 'bottom' ? hh : y;
  const n = (v: number, size: number) => (size > 0 ? Math.round(((v + size / 2) / size) * 1e6) / 1e6 : 0.5);
  return { nx: n(ex, o.w), ny: n(ey, o.h) };
}

const add = (p: Point, n: Point, k: number): Point => ({ x: p.x + n.x * k, y: p.y + n.y * k });

/** Without a side, an end leaves along the dominant axis towards the other end. */
function outward(side: Side | null | undefined, from: Point, to: Point): Point {
  if (side) return NORMAL[side];
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return Math.abs(dx) >= Math.abs(dy) ? { x: dx >= 0 ? 1 : -1, y: 0 } : { x: 0, y: dy >= 0 ? 1 : -1 };
}

function boundsOf(points: readonly Point[]): Rect {
  return {
    minX: Math.min(...points.map((p) => p.x)),
    minY: Math.min(...points.map((p) => p.y)),
    maxX: Math.max(...points.map((p) => p.x)),
    maxY: Math.max(...points.map((p) => p.y)),
  };
}

const fmt = (p: Point) => `${+p.x.toFixed(2)},${+p.y.toFixed(2)}`;

/**
 * SVG path of a connector from `a` to `b` (world points): `points` are its vertices (control points for a curve),
 * `samples` a polyline close to it (hit-testing, previews), `bounds` contains the whole drawing.
 */
export function connectorPath(
  a: Point,
  b: Point,
  routing: Routing,
  sides: { a?: Side | null; b?: Side | null } = {},
  bend = 0,
): { d: string; points: Point[]; samples: Point[]; bounds: Rect } {
  if (bend !== 0 && routing !== 'elbow') {
    // A quadratic through the bent midpoint: control = 2M − (a + b) / 2.
    const m = bentMidpoint(a, b, bend);
    const c = { x: 2 * m.x - (a.x + b.x) / 2, y: 2 * m.y - (a.y + b.y) / 2 };
    const samples: Point[] = [];
    for (let i = 0; i <= CURVE_SAMPLES; i++) {
      const t = i / CURVE_SAMPLES;
      const u = 1 - t;
      samples.push({ x: u * u * a.x + 2 * u * t * c.x + t * t * b.x, y: u * u * a.y + 2 * u * t * c.y + t * t * b.y });
    }
    samples[0] = a;
    samples[CURVE_SAMPLES] = b;
    return { d: `M${fmt(a)} Q${fmt(c)} ${fmt(b)}`, points: [a, c, b], samples, bounds: boundsOf(samples) };
  }
  if (routing === 'straight') {
    const points = [a, b];
    return { d: `M${fmt(a)} L${fmt(b)}`, points, samples: points, bounds: boundsOf(points) };
  }
  const na = outward(sides.a, a, b);
  const nb = outward(sides.b, b, a);
  if (routing === 'curved') {
    const k = Math.max(40, Math.hypot(b.x - a.x, b.y - a.y) / 3);
    const c1 = add(a, na, k);
    const c2 = add(b, nb, k);
    const samples: Point[] = [];
    for (let i = 0; i <= CURVE_SAMPLES; i++) {
      const t = i / CURVE_SAMPLES;
      const u = 1 - t;
      samples.push({
        x: u * u * u * a.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * b.x,
        y: u * u * u * a.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * b.y,
      });
    }
    samples[0] = a;
    const points = [a, c1, c2, b];
    return { d: `M${fmt(a)} C${fmt(c1)} ${fmt(c2)} ${fmt(b)}`, points, samples, bounds: boundsOf(points) };
  }
  // Elbow: a stub out of each side, then horizontal-vertical-horizontal (or the transpose).
  const sa = add(a, na, STUB);
  const sb = add(b, nb, STUB);
  const horizontalFirst = na.x !== 0;
  const mid = horizontalFirst
    ? [{ x: (sa.x + sb.x) / 2, y: sa.y }, { x: (sa.x + sb.x) / 2, y: sb.y }]
    : [{ x: sa.x, y: (sa.y + sb.y) / 2 }, { x: sb.x, y: (sa.y + sb.y) / 2 }];
  const raw = [a, sa, ...mid, sb, b];
  const points = raw.filter((p, i) => i === 0 || p.x !== raw[i - 1]!.x || p.y !== raw[i - 1]!.y);
  return { d: `M${points.map(fmt).join(' L')}`, points, samples: points, bounds: boundsOf(points) };
}

/** The midpoint of the chord `ab` pushed by `bend` chord lengths along its left-hand normal (−dy, dx). */
function bentMidpoint(a: Point, b: Point, bend: number): Point {
  return { x: (a.x + b.x) / 2 - bend * (b.y - a.y), y: (a.y + b.y) / 2 + bend * (b.x - a.x) };
}

/** The point halfway along a connector's drawing: where its label and its bend handle sit. */
export function connectorMidpoint(a: Point, b: Point, routing: Routing, sides: { a?: Side | null; b?: Side | null } = {}, bend = 0): Point {
  if (bend !== 0 && routing !== 'elbow') return bentMidpoint(a, b, bend);
  const { samples } = connectorPath(a, b, routing, sides);
  if (routing === 'curved') return samples[CURVE_SAMPLES / 2]!;
  // Straight and elbow: half the polyline's length.
  const lens = samples.slice(1).map((p, i) => Math.hypot(p.x - samples[i]!.x, p.y - samples[i]!.y));
  let rest = lens.reduce((s, l) => s + l, 0) / 2;
  for (let i = 0; i < lens.length; i++) {
    if (rest <= lens[i]! && lens[i]! > 0) {
      const t = rest / lens[i]!;
      return { x: samples[i]!.x + (samples[i + 1]!.x - samples[i]!.x) * t, y: samples[i]!.y + (samples[i + 1]!.y - samples[i]!.y) * t };
    }
    rest -= lens[i]!;
  }
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export const MAX_BEND = 2;

/** The bend that puts a connector's midpoint at `p`: signed distance from the chord over its length, in [−2, 2]. */
export function bendFor(a: Point, b: Point, p: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return 0;
  const k = ((p.x - (a.x + b.x) / 2) * -dy + (p.y - (a.y + b.y) / 2) * dx) / l2;
  return Math.max(-MAX_BEND, Math.min(MAX_BEND, k));
}
